/**
 * ColorVisibilityToggles — a row of small colored circles that toggle the
 * visibility of each compiled color in the preview, without recompiling.
 *
 * Visible:  filled circle in the actual RGBA color.
 * Hidden:   dark-gray fill + border ring in the actual color.
 * Ghost:    same as above but the swatch is rendered at reduced opacity.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

/** Stable string key for an RGBA tuple, rounded to 3 decimal places. */
export function toColorKey(rgba: [number, number, number, number]): string {
  return rgba.map((v) => Math.round(v * 1000) / 1000).join(',');
}

/** Convert a 0-1 RGBA tuple to a CSS `rgba(...)` string. */
function toCss(rgba: [number, number, number, number], alpha = 1): string {
  return `rgba(${Math.round(rgba[0] * 255)},${Math.round(rgba[1] * 255)},${Math.round(rgba[2] * 255)},${alpha})`;
}

/** Convert a 0-1 RGB tuple to a 6-digit hex string (e.g. "#ff6a00"). */
function toHex(rgba: [number, number, number, number]): string {
  return (
    '#' +
    [rgba[0], rgba[1], rgba[2]]
      .map((v) =>
        Math.round(v * 255)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

export type CompiledColor = {
  key: string;
  rgba: [number, number, number, number];
  isGhost?: boolean;
};

interface ColorVisibilityTogglesProps {
  colors: CompiledColor[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
}

export function ColorVisibilityToggles({
  colors,
  hiddenKeys,
  onToggle,
}: ColorVisibilityTogglesProps) {
  if (colors.length === 0) return null;

  return (
    <div className="flex items-center gap-1">
      {colors.map((c) => {
        const hidden = hiddenKeys.has(c.key);
        const hex = toHex(c.rgba);
        const colorCss = toCss(c.rgba, 1);
        const swatchOpacity = c.isGhost ? 0.65 : 1;

        return (
          <TooltipProvider key={c.key} delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onToggle(c.key)}
                  className="flex h-5 w-5 items-center justify-center rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  style={{ opacity: swatchOpacity }}
                  aria-label={`${hidden ? 'Show' : 'Hide'} color ${hex}`}
                  aria-pressed={!hidden}
                >
                  <span
                    className="block h-3 w-3 rounded-full"
                    style={
                      hidden
                        ? {
                            background: 'rgb(45,45,45)',
                            border: `1.5px solid ${colorCss}`,
                          }
                        : {
                            background: colorCss,
                          }
                    }
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={4}
                className="border-border bg-background text-foreground"
              >
                <span className="font-mono text-xs">
                  {hex}
                  {c.isGhost ? ' (ghost)' : ''}
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}
